import { isHttpErrorDetailsPayload, type ProviderAuthenticationIntentResponse } from '@unipile/types';
import {
  Button,
  Input,
  LoginWizardContainer,
  useStartProviderAuthIntent,
  useSolveCheckpoint,
  type WizardComponent,
  LocationConfig,
  Field,
  FieldLabel,
  FieldGroup,
  CancelButton,
  ScopesConsentScreen,
  useScopesConsent,
} from '@unipile/ui';
import { isAxiosError } from 'axios';
import { Loader2Icon, RefreshCcw } from 'lucide-react';
import qrcode from 'qrcode';
import { useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

type TwoFactorAuthenticationCode = { code: string };
type Step = 'scopes_consent' | 'two_fa' | 'qr_code';

export const Wizard: WizardComponent = ({ onSuccess, onError, onCancel }) => {
  const { t } = useTranslation();

  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Scopes consent management - must be shown BEFORE QR code (scanning = immediate connection)
  const { scopes, hasScopes, brandName } = useScopesConsent({ provider: 'telegram' });

  // Determine initial step: if there are scopes, show consent first, otherwise go to 2FA
  const [step, setStep] = useState<Step>(hasScopes ? 'scopes_consent' : 'two_fa');
  const [twoFaCode, setTwoFaCode] = useState('');

  const handleTwoFactorCode = (code: string) => {
    setTwoFaCode(code);
    setStep('qr_code');
    setTimeout(() => authenticate.mutate({ data: { provider: 'telegram', credentials: { qrcode: true, twofa: code } } }), 0);
  };

  const handleSuccess = (response: ProviderAuthenticationIntentResponse) => {
    if (response.object === 'AuthenticationQrCode') {
      qrcode.toCanvas(canvasRef.current, response.qrcode);
      solve.mutate({ data: { code: twoFaCode, intent_id: response.intent_id } });
    }
    if (response.object === 'Account') onSuccess(response);
  };

  const handleError = (error: unknown) => {
    if (
      isAxiosError(error) &&
      isHttpErrorDetailsPayload(error.response?.data) &&
      error.response.data.type === 'provider/invalid_credentials'
    ) {
      setStep('two_fa');
    } else {
      onError(error);
    }
  };

  const authenticate = useStartProviderAuthIntent({ mutationConfig: { onSuccess: handleSuccess, onError: handleError } });
  const solve = useSolveCheckpoint({
    mutationConfig: {
      onSuccess: handleSuccess,
      onError: handleError,
    },
  });

  // Show scopes consent screen BEFORE QR code
  if (step === 'scopes_consent') {
    return (
      <LoginWizardContainer>
        <ScopesConsentScreen scopes={scopes} brandName={brandName} onAccept={() => setStep('two_fa')} onCancel={onCancel} />
      </LoginWizardContainer>
    );
  }

  return (
    <LoginWizardContainer>
      {step === 'two_fa' && <TwoFactorAuthentication onSuccess={handleTwoFactorCode} />}

      {step === 'two_fa' && !hasScopes && <CancelButton onCancel={onCancel} />}

      {step === 'qr_code' && (
        <div>
          <ol className="my-6 ml-6 list-decimal [&>li]:mt-2">
            <li>{t('Wizard.step1', { ns: 'telegram' })}</li>
            <li>{t('Wizard.step2', { ns: 'telegram' })}</li>
            <li>{t('Wizard.step3', { ns: 'telegram' })}</li>
            <li>{t('Wizard.step4', { ns: 'telegram' })}</li>
          </ol>
          <div className="relative mx-auto flex h-[276px] w-[276px] flex-col justify-center">
            {authenticate.isPending && (
              <div className="bg-muted absolute z-10 flex h-full w-full items-center justify-center rounded">
                <Loader2Icon className="animate-spin" size={40} />
              </div>
            )}
            {solve.isError && (
              <div className="absolute flex h-full w-full items-center justify-center rounded bg-white/95">
                <Button
                  onClick={() =>
                    setTimeout(
                      () =>
                        authenticate.mutate({ data: { provider: 'telegram', credentials: { qrcode: true, twofa: twoFaCode } } }),
                      0,
                    )
                  }
                >
                  <RefreshCcw className="mr-2 size-4" />
                  {t('Wizard.clickToReload', { ns: 'telegram' })}
                </Button>
              </div>
            )}

            <canvas ref={canvasRef} width={276} height={276} className="mx-auto"></canvas>
          </div>
        </div>
      )}
    </LoginWizardContainer>
  );
};

function TwoFactorAuthentication({ onSuccess }: { onSuccess: (code: string) => void }) {
  const { t } = useTranslation();

  const form = useForm<TwoFactorAuthenticationCode>({
    defaultValues: {
      code: '',
    },
  });

  const onSubmit = async (data: TwoFactorAuthenticationCode) => {
    onSuccess(data.code);
  };

  const onSkip = async () => {
    onSuccess('');
  };

  return (
    <form onSubmit={form.handleSubmit(onSubmit)}>
      <FieldGroup>
        <Controller
          control={form.control}
          name="code"
          rules={{ required: true }}
          render={({ field }) => (
            <Field>
              <FieldLabel>{t('Wizard.form.two_fa_code.label', { ns: 'telegram' })}</FieldLabel>
              <Input type="password" placeholder="Code" {...field} />
            </Field>
          )}
        />
        <LocationConfig provider="telegram" />
        <div className="flex justify-between gap-4">
          <Button className="flex-1" type="button" onClick={onSkip}>
            {t('Wizard.form.no_2fa.label', { ns: 'telegram' })}
          </Button>

          <Button className="flex-1" type="submit" disabled={!form.formState.isValid}>
            {t('Common.next')}
          </Button>
        </div>
      </FieldGroup>
    </form>
  );
}
